/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@/components/Button';
import { type ReduxState } from '@/types';
import { Label } from '@/features/i18n';
import { useLocale } from '@/hooks/useLocale';
import { SmartLink as Link } from '@/utils/smartLink';
import { routePaths } from '@/constants/routePaths';
import { openChatWindow } from '@/features/proactiveChat';
import { trackChatLink } from '@/utils/analytics/track/trackChatLink';
import styles from './styles.module.scss';
import { countryCodes } from '@/constants/locale';

const labelMap = {
  lblNoResults: 'label_Sorry,_no_results_were_found_for',
  lblTryAgain: 'label_Please_try_another_search',
  lblSearchTips: 'label_Search_NullResults_SearchTips',
  lblSpelling: 'label_Search_NullResults_DoubleCheckYourSpelling',
  lblSingleWords: 'label_Search_NullResults_TryUsingSingleWords',
  lblLessSpecific: 'label_Search_NullResults_TrySearchingForAnItemThatIsLessSpecific',
  lblNeedHelp: 'label_Need_Help?',
  lblContactUs: 'label_Contact_Us',
  lblChatNow: 'label_Chat_Now',
  lblOr: 'label_or',
  lblOpenaNewWindow: 'label_OpenaNewWindow',
  lblForYour: 'label_Search_for_your',
};

export const SearchNoResults = ({
  queryDisplayLabel,
  vehicleName,
}: {
  queryDisplayLabel?: string;
  vehicleName?: string;
}) => {
  const isChatAgentAvailable = useSelector(
    (state: ReduxState) => state.appData.isChatAgentAvailable
  );
  const locale = useLocale();
  const isBR = locale === countryCodes.ptBr;

  const showSearchLiveChat = () => {
    openChatWindow();
    /* Analytics start */

    if (window.utag_data?.pageName) {
      trackChatLink(window.utag_data.pageName);
    }
    /* Analytics end */
  };
  return (
    <div className={styles.noResultsContainer}>
      <div className={styles.noResultsTryAnother}>
        <span className={styles.noResultsHeader}>
          <Label label={labelMap.lblNoResults} />{' '}
          <strong>
            {'"'}
            {queryDisplayLabel}
            {'"'}
          </strong>
          {!vehicleName ? (
            '.'
          ) : (
            <span>
              {' '}
              <Label label={labelMap.lblForYour} /> <strong>{vehicleName}</strong>
            </span>
          )}
        </span>
      </div>
      <hr />
      <span className={styles.searchTips}>
        <Label label={labelMap.lblSearchTips} upper />
      </span>
      <div>
        <span className={styles.tipsList}>
          <Label label={labelMap.lblSpelling} />
        </span>
        <span className={styles.tipsList}>
          <Label label={labelMap.lblLessSpecific} />
        </span>
        <span className={styles.tipsList}>
          <Label label={labelMap.lblSingleWords} />
        </span>
        {!isBR ? (
          <span className={styles.tipsList}>
            <Label label={labelMap.lblNeedHelp} />{' '}
            <Link to={routePaths.contactUs} className={styles.needHelpContactUs}>
              <Label label={labelMap.lblContactUs} />
            </Link>{' '}
            {isChatAgentAvailable ? (
              <Fragment>
                <Label label={labelMap.lblOr} />{' '}
                <Button
                  id="searchliveChat"
                  variant="link"
                  data-testid="searchliveChat"
                  customClass={styles.needHelpChat}
                  onClick={showSearchLiveChat}
                >
                  <span>
                    <Label label={labelMap.lblChatNow} />
                  </span>
                  <span className={styles.sr}>
                    <Label label={labelMap.lblOpenaNewWindow} />
                  </span>
                </Button>
              </Fragment>
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
  );
};
